/**
 * PLAN-DISC-SCOPE-01 (Seth 2026-10-28, "GO with those"): what the member repair
 * discount touches.
 *
 * The plan is sold as "15% off repairs", and a customer reads that as fixing what
 * is broken — so the tier applies to REPAIR PARTS AND LABOR only. It never touches
 * fees (the diagnostic has its own member price), the plan line, equipment,
 * accessories and IAQ products sold at the visit (air scrubbers, humidifiers,
 * thermostats, UV), chargeable consumables (filters, bulbs) or install materials.
 * The same call Seth made for installs (a plan never re-prices equipment), applied
 * at the smaller scale of a service invoice. ServiceTitan and FieldEdge set their
 * membership discounts per pricebook category for exactly this reason.
 *
 * A line can be switched the other way by hand (the office or the tech decides to
 * include a product, or to leave a part at list) — `memberDiscount` on the line is
 * that explicit choice; null means "the rule decides". Today the choice lives for
 * the session: a reopened draft recomputes from the rule (PLAN-DISC-LINE-PERSIST).
 *
 * Example (3-year member, 15%): $109 diagnostic + $500 inducer repair + $499 Air
 * Scrubber → the discount is 15% of $500 = $75; the scrubber stays at list.
 *
 * Shared by the office Invoice Builder, the tech app's invoice, and the Plus Plan
 * credit (lib/planCredit). The homeowner proposal page applies the same rule
 * server-side (quote_plan_offer_json → repair_line_ids).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/** Pricebook categories that are equipment, accessories / IAQ, consumables or materials — never repair-discounted. */
export const MEMBER_EXCLUDED_CATEGORIES = new Set<string>([
  // whole systems
  "furnace", "condenser", "air conditioner", "heat pump", "mini-split", "mini split", "rooftop", "boiler", "water heater",
  "magic-pak", "magic pak", "fan coil", "unit heater", "ptac", "comfort-pak", "spacepak", "make up air", "magic aire", "first company",
  "blower cabinet",
  // accessories / IAQ
  "accessory", "humidifier", "dehumidifier", "air cleaner", "uv", "erv / hrv", "erv/hrv", "thermostat", "zoning", "water softener",
  "chimney liner",
  // consumables and install materials
  "filter", "lineset", "condenser pad", "shop supply", "plumbing",
]);

export interface MemberPricingItem { category?: string | null; equip_type?: string | null }
export interface MemberPricingLine {
  kind: string;
  description?: string | null;
  item_id?: string | null;
  /** The explicit per-line choice; null / undefined = the rule decides. */
  memberDiscount?: boolean | null;
}

/** Lines that are the plan itself, or a visit fee written as a custom line. */
export const NOT_REPAIR_TEXT = /\b(plus plan|maintenance plus|membership|member discount|repair discount|diagnostic|trip charge|service call|dispatch fee)\b/i;

/** Does the member repair % apply to this line by the RULE (before any hand choice)? */
export function memberDiscountByRule(l: MemberPricingLine, item?: MemberPricingItem | null): boolean {
  if (l.kind === "fee") return false;
  if (NOT_REPAIR_TEXT.test(l.description ?? "")) return false;
  if (l.kind === "labor") return true;
  if (item) {
    if (item.equip_type) return false;
    const cat = (item.category ?? "").trim().toLowerCase();
    if (cat && MEMBER_EXCLUDED_CATEGORIES.has(cat)) return false;
  }
  return true; // repair parts, labor, and custom repair lines ("Flush drain line")
}

/** The rule, unless a hand choice overrides it. */
export function memberDiscountEligible(l: MemberPricingLine, item?: MemberPricingItem | null): boolean {
  if (l.memberDiscount === true || l.memberDiscount === false) return l.memberDiscount;
  return memberDiscountByRule(l, item);
}

/** The pricebook category + equipment flag for the items on an invoice's lines (one query, cached). */
export function useItemCategories(ids: Array<string | null | undefined>) {
  const key = [...new Set(ids.filter((x): x is string => !!x))].sort();
  return useQuery({
    queryKey: ["item_categories", key.join(",")],
    enabled: key.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Record<string, MemberPricingItem>> => {
      const { data, error } = await supabase.from("item").select("id, category, equip_type").in("id", key);
      if (error) throw error;
      const out: Record<string, MemberPricingItem> = {};
      for (const r of (data ?? []) as Array<{ id: string; category: string | null; equip_type: string | null }>) out[r.id] = { category: r.category, equip_type: r.equip_type };
      return out;
    },
  });
}

/** The dollars the member % applies to: printed, un-waived, eligible lines. */
export function memberEligibleSubtotal<L extends MemberPricingLine & { qty: number; unitPrice: number; print?: boolean; waived?: boolean }>(
  lines: L[],
  items: Record<string, MemberPricingItem> | undefined,
): number {
  return lines
    .filter((l) => (l.print ?? true) && !l.waived && memberDiscountEligible(l, l.item_id ? items?.[l.item_id] : null))
    .reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
}
